/** 投放书籍风险提示：沿用浅色告警卡与状态标签，列表和详情共享目标 App 状态说明。 */
import type { LandingProjectBookVisibilityWarningDto } from '@novel-admin/shared';
import type { ReactNode } from 'react';
import { fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';

const visibilityLabels = {
  DELISTED: '已下架',
  PAYING_ONLY: '非公开 · 仅付费用户可见',
  UNLOCKED_ONLY: '非公开 · 仅已解锁用户可见',
};
export function LandingProjectBookVisibilityAlert({
  warnings,
  total = warnings.length,
  onOpen,
  children,
  error,
}: {
  warnings: LandingProjectBookVisibilityWarningDto[];
  total?: number;
  onOpen?: (id: string) => void;
  children?: ReactNode;
  error?: boolean;
}) {
  if (!total && !error) return null;
  return (
    <section
      role="alert"
      aria-label="投放书籍可见性风险"
      className="card vstack"
      style={{
        background: 'var(--neg-soft)',
        borderColor: 'color-mix(in srgb, var(--neg) 25%, var(--border))',
        borderLeft: '4px solid var(--neg)',
        color: 'var(--fg)',
        padding: 16,
        gap: 10,
      }}
    >
      <div className="hstack" style={{ gap: 8, fontWeight: 700, color: 'var(--neg)' }}>
        <Icons.IconAlert size={18} />
        <span>
          投放风险：
          {total > 0 ? `${total} 项关联书籍已下架或非公开可见` : '书籍可见状态暂时无法核验'}
        </span>
      </div>
      <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
        请核对目标 App 的可见范围，确认公开上架后再继续广告投放。
      </div>
      {error && <div>可见状态更新失败，当前信息可能已过期，请重新核验。{children}</div>}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
        {warnings.map((row) => (
          <li
            key={`${row.project_id}:${row.app_id}`}
            style={{
              overflowWrap: 'anywhere',
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '10px 12px',
            }}
          >
            <div
              className="hstack"
              style={{
                gap: 8,
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              <strong>{row.cn_title || row.book_title}</strong>
              <span
                className={`chip ${row.visibility === 'DELISTED' ? 'chip-neg' : 'chip-warn'}`}
                style={{ whiteSpace: 'normal' }}
              >
                {visibilityLabels[row.visibility]}
              </span>
            </div>
            <div
              className="hstack"
              style={{
                gap: 8,
                marginTop: 4,
                fontSize: 'var(--text-sm)',
                color: 'var(--fg-muted)',
                flexWrap: 'wrap',
              }}
            >
              <span className="mono">{row.book_code}</span>
              <span>{fmtLang(row.locale)}</span>
              <span>
                目标 App：{row.app_name} · {row.product_name}
              </span>
            </div>
            <div
              className="hstack"
              style={{
                gap: 8,
                marginTop: 6,
                fontSize: 'var(--text-sm)',
                color: 'var(--fg-muted)',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                项目：{row.project_name} · <span className="mono">{row.project_code}</span> ·{' '}
                {row.project_status === 'ARCHIVED' ? '已归档' : '使用中'}
              </span>
              {onOpen && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => onOpen(row.project_id)}
                >
                  查看项目 <Icons.IconChevRight size={12} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {!error && children}
    </section>
  );
}
